import { Diagnostic } from 'web-core-src/classes/error-handling';

export interface DeepCopyContext {
    target: any;
    source: any;
}

const __decoratorContainer_deepCopyClassName = new Map<string, any>();
export function deepCopyClassName<T>(type: { new (): T }, dictionaryKeyType?: { new (): T }) {
    return function (target: any, propertyName: string) {
        let map = __decoratorContainer_deepCopyClassName.get(target.constructor.name);
        if (!map) {
            map = {};
            __decoratorContainer_deepCopyClassName.set(target.constructor.name, map);
        }

        if (dictionaryKeyType) {
            map[propertyName] = { type, dictionaryKeyType: dictionaryKeyType };
        } else {
            map[propertyName] = type;
        }
    };
}
export class DeepCopyHelper {
    public trimString: boolean;
    public errors: string;
    public static parent: any;

    constructor(trimString?: boolean) {
        this.trimString = trimString;
    }

    public deepCopy(context: DeepCopyContext): void {
        Object.keys(context.source).forEach((propertyName: string): void => {
            let target = context.target;
            let source = context.source;

            if (source.hasOwnProperty(propertyName)) {
                //if there is no setter, we don't throw exception
                try {
                    let isPrimitiveType: boolean = this.isPrimitiveType(target[propertyName]) || this.isPrimitiveType(source[propertyName]);
                    let isArrayType: boolean = Object.prototype.toString.call(target[propertyName]) === '[object Array]' || Object.prototype.toString.call(source[propertyName]) === '[object Array]';
                    let isObjectType = typeof target[propertyName] === 'object' || typeof source[propertyName] === 'object';

                    /**
                     * Retrieves the class corresponding to the target type from the decorator container.
                     */
                    let targetConstructorName = target.constructor.name;
                    let classMap = __decoratorContainer_deepCopyClassName.get(targetConstructorName);

                    let allParents = [];
                    let parentClassMap = null;
                    if (!isPrimitiveType) {
                        /**
                         * If the type is not a primitive type, retrieve all parent classes associated with the target type.
                         * Decorators do not inherit between classes. Therefore, if a parent class key has been registered
                         * with the given class, this is the only way to find it.
                         */
                        allParents = this.getClassHierarchy(target.constructor);
                        parentClassMap = this.findParentClassMapForProperty(allParents, propertyName);
                    }

                    if (isPrimitiveType) {
                        this.primitiveTypeHandler(context, propertyName);
                    } else if (isArrayType) {
                        target[propertyName] = [];
                        if (classMap && classMap[propertyName]) {
                            // if the decorator container contains a matching type for the property based on the target constructor name
                            this.arrayTypeHandler(target[propertyName], source[propertyName], classMap[propertyName]);
                        } else if (parentClassMap && parentClassMap[propertyName]) {
                            // if no matching type is found by the target constructor name, but one is found based on one of its parent classes
                            this.arrayTypeHandler(target[propertyName], source[propertyName], parentClassMap[propertyName]);
                        } else {
                            this.errors += ' deepCopy - No ArrayType is set' + targetConstructorName + ' - ' + propertyName + '\r\n';
                        }
                    } else if (isObjectType) {
                        if (classMap && classMap[propertyName]) {
                            // if the decorator container contains a matching type for the property based on the target constructor name
                            this.objectOrDictionaryTypeHandler(context, propertyName, classMap[propertyName]);
                        } else if (parentClassMap && parentClassMap[propertyName]) {
                            // if no matching type is found by the target constructor name, but one is found based on one of its parent classes
                            this.objectOrDictionaryTypeHandler(context, propertyName, parentClassMap[propertyName]);
                        } else {
                            this.errors += ' deepCopy - Missing decorator for subobject: classMap is undefined for ' + target.constructor.name + ' - ' + propertyName + `. Make sure the subobject class is properly decorated before use.\r\n`;
                        }
                    } else {
                        this.errors += ' deepCopy - No default value or ObjectType or ArrayType is set' + target.constructor.name + ' - ' + propertyName + '\r\n';
                    }
                } catch (e) {
                    Diagnostic.handleError(e);
                }
                if (this.errors) {
                    throw new Error(this.errors);
                }
            }
        });
    }

    private findParentClassMapForProperty(constructorNames: string[], propertyName: string): any {
        for (let constructor of constructorNames) {
            let classMap = __decoratorContainer_deepCopyClassName.get(constructor);
            if (!classMap) {
                continue;
            }
            if (classMap[propertyName]) {
                return classMap;
            }
        }

        return null;
    }

    private getClassHierarchy(constructor: Function): string[] {
        let names: string[] = [];
        let current: any = constructor;

        while (current && current.name && current.name !== 'Object') {
            names.push(current.name);
            current = Object.getPrototypeOf(current);
        }

        return names;
    }

    private isPrimitiveType(obj: any): boolean {
        return this.isString(obj) || typeof obj === 'number' || typeof obj === 'boolean' || (obj && obj.constructor.name === 'Date');
    }

    private isString(obj: any): boolean {
        return typeof obj === 'string';
    }

    private primitiveTypeHandler(context: DeepCopyContext, propertyName: string): void {
        let propertyValue = context.source[propertyName];
        let shouldTrim = this.trimString && context.source[propertyName] != null && (this.isString(context.target[propertyName]) || this.isString(context.source[propertyName]));
        if (shouldTrim) {
            propertyValue = propertyValue.trim();
        }
        context.target[propertyName] = propertyValue;
    }

    private arrayTypeHandler(targetArray: any[], sourceArray: any[], sourceItemType: any): void {
        /**
         * Iterate through the source array that needs to be copied.
         * If the current element is of a primitive type, simply add it to the target array.
         * If it's not a primitive, recursively call deepCopy with a new target object,
         * and then push the copied data into the target array.
         */
        for (let item of sourceArray) {
            if (this.isPrimitiveType(item)) {
                targetArray.push(item);
            } else {
                let targetArrayItem: any = new sourceItemType();
                let params: DeepCopyContext = {
                    target: targetArrayItem,
                    source: item,
                };

                this.deepCopy(params);
                targetArray.push(targetArrayItem);
            }
        }
    }

    private dictionaryTypeHandler(context: DeepCopyContext, propertyName: string, entry: any): void {
        /**
         * Extract the dictionary key type and the value type obtained from the decorator container.
         */
        let keyType = this.getDictionaryPrimitiveKeyType(entry.dictionaryKeyType);
        let valueType = entry.type;

        for (let [key, value] of Object.entries(context.source[propertyName])) {
            let newKey = this.isString(keyType) ? key : keyType(key);

            if (Array.isArray(value)) {
                /**
                 * If the value is an array type, create an empty array for the new key
                 * and pass it to the arrayTypeHandler function for further processing.
                 */
                context.target[propertyName][newKey] = [];
                this.arrayTypeHandler(context.target[propertyName][newKey], value, valueType);
            } else if (this.isPrimitiveType(valueType)) {
                /**
                 * If the value is a primitive type, simply assign the value to the corresponding key.
                 */
                context.target[propertyName][newKey] = value;
            } else {
                /**
                 * If the value is an object, create a new object under the target's key
                 * and recursively invoke deepCopy on it.
                 */
                if (!context.target[propertyName][newKey]) {
                    context.target[propertyName][newKey] = new valueType();
                }
                let params: DeepCopyContext = {
                    target: context.target[propertyName][newKey],
                    source: value,
                };

                this.deepCopy(params);
            }
        }
    }

    private getDictionaryPrimitiveKeyType(keyType: any): any {
        /**
         * A record can only contain primitive types.
         */
        if (keyType === String) keyType = 'string';
        if (keyType === Number) keyType = 'number';
        if (keyType === Boolean) keyType = 'boolean';

        return keyType;
    }

    private objectTypeHandler(context: DeepCopyContext, propertyName: string, type: any): void {
        if (!context.target[propertyName]) {
            context.target[propertyName] = new type();
        }
        /**
         * Recursively call deepCopy, where the target is the object assigned to the given propertyName key,
         * and the source is the object associated with the same propertyName key.
         */
        let params: DeepCopyContext = {
            target: context.target[propertyName],
            source: context.source[propertyName],
        };

        this.deepCopy(params);
    }

    private objectOrDictionaryTypeHandler(context: DeepCopyContext, propertyName: string, entry: any): void {
        /**
         * entry: the class value retrieved from the decorator container.
         */
        if (entry.type && entry.dictionaryKeyType) {
            /**
             * If the entry is a dictionary, it has both a type and a dictionaryKeyType.
             */
            this.dictionaryTypeHandler(context, propertyName, entry);
        } else if (typeof entry === 'function') {
            /**
             * If the entry's type is a function, then it represents an object.
             */
            this.objectTypeHandler(context, propertyName, entry);
        }
    }
}
